import React, { useState, useEffect } from 'react';
import { walletService } from '../../../services/walletService';
import Icon from '../../../components/AppIcon';
import { formatPrice } from '../../../utils/formatters';

const WalletManager = () => {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [balanceData, transactionsData] = await Promise.all([
        walletService.getBalance(),
        walletService.getTransactions()
      ]);
      setBalance(balanceData);
      setTransactions(transactionsData);
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError('Failed to load wallet information.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="bg-primary text-white p-8 rounded-2xl shadow-soft relative overflow-hidden">
        <div className="relative z-10">
          <p className="text-white/90 text-xs font-bold uppercase tracking-wider mb-1">Total Store Credit Balance</p>
          <h2 className="text-4xl font-bold mb-4">{formatPrice(balance)}</h2>
          <div className="flex items-center space-x-2 text-sm bg-black/20 rounded-lg px-4 py-2 w-fit border border-white/10">
            <Icon name="Info" size={16} className="flex-shrink-0" />
            <span className="font-medium">Applied automatically at checkout</span>
          </div>
        </div>
        {/* Subtle background decoration */}
        <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-white/5 rounded-full"></div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-soft">
        <div className="px-6 py-4 border-b border-border bg-muted/30">
          <h3 className="text-base font-bold text-foreground">Credit Transaction History</h3>
        </div>
        
        {error ? (
          <div className="p-8 text-center text-error font-medium">{error}</div>
        ) : transactions.length === 0 ? (
          <div className="p-12 text-center bg-card">
            <Icon name="History" size={48} className="mx-auto mb-4 text-muted-foreground opacity-20" />
            <p className="text-muted-foreground font-medium">No transactions yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {transactions.map((tx) => (
              <div key={tx.id} className="px-6 py-4 flex items-center justify-between hover:bg-muted/20 transition-smooth">
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-lg ${tx.type === 'credit' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
                    <Icon name={tx.type === 'credit' ? 'TrendingUp' : 'TrendingDown'} size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground leading-tight">{tx.description || (tx.type === 'credit' ? 'Refund Added' : 'Credit Applied')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(tx.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className={`text-base font-bold tabular-nums ${tx.type === 'credit' ? 'text-success' : 'text-destructive'}`}>
                  {tx.type === 'credit' ? '+' : '-'}{formatPrice(tx.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletManager;
